import Axios from 'axios';
import { RenderContentItem } from './rendering/coordinators/RenderContentItem';
import { GetContentItem } from './content/coordinators/GetContentItem';
import { ContentMapper } from './content/mapper/ContentMapper';
/**
 * Amplience [Content Delivery API](https://docs.amplience.net/integration/deliveryapi.html?h=delivery) client.
 *
 * This client is intended to be used by end user applications to fetch content so that it can be displayed to users.
 *
 * You must provide some basic account information in order to create an instance of ContentClient.
 *
 * Example:
 *
 * ```typescript
 * const client = new ContentClient({
 *    account: 'test'
 * });
 * ```
 *
 * You may override other settings when constructing the client but if no additional configuration is provided sensible defaults will be used.
 */
export class ContentClient {
    /**
     * Creates a Delivery API Client instance. You must provide a configuration object with the account you wish to fetch content from.
     * @param config Client configuration options
     */
    constructor(config) {
        this.config = config;
        if (!config) {
            throw new TypeError('Parameter "config" is required');
        }
        if (!config.account) {
            throw new TypeError('Parameter "config" must contain a valid "account" name');
        }
        if (config.stagingEnvironment &&
            config.stagingEnvironment.indexOf('://') !== -1) {
            throw new TypeError('Parameter "stagingEnvironment" should be a hostname not a URL');
        }
        this.contentClient = this.createContentClient(config);
        this.contentMapper = this.createContentMapper(config);
    }
    /**
     * This function will load a Content Item or Slot by id and return a Promise of the result.
     * If the content is not found the promise will reject with an error.
     * If the content is found the promise will resolve with a parsed version of the content with all dependencies.
     *
     * The content body will match the format defined by your content type, however keep in mind that if you have evolved your content type some published content may still be in the older format.
     *
     * Some pre-processing is applied to the content body to make it easier to work with:
     *
     * * Linked content items are joined together into the root content item to create a single JSON document.
     * * Media references (images and videos) are replaced with wrapper objects [[Image]] and [[Video]] which provide helper functions.
     * * Content created using V1 of the CMS will be upgraded to the V2 format.
     *
     * You can convert the content back to plain JSON by calling the toJSON() function on the returned ContentItem.
     * @typeparam T The type of content returned. This is optional and by default the content returned is assumed to be “any”.
     * @param id Unique id of the Content Item or Slot to load
     */
    getContentItem(contentItemId) {
        return new GetContentItem(this.config, this.contentClient, this.contentMapper).getContentItem(contentItemId);
    }
    /**
     * Converts a Content Item or Slot into a custom format (e.g. HTML / XML) by applying a template server side.
     * @param contentItemId Unique id of the Content Item or Slot to convert using the rendering service
     * @param templateName Name of the template to render the content item or slot with. The template must be setup in your account
     * @param customParameters Custom parameters which will be sent to the rendering API and made avaliable to your template
     */
    renderContentItem(contentItemId, templateName, customParameters) {
        return new RenderContentItem(this.config, this.contentClient).renderContentItem(contentItemId, templateName, customParameters);
    }
    /**
     * Create network client to make requests to the content delivery service
     * @param config
     */
    createContentClient(config) {
        const client = Axios.create({
            adapter: config.adaptor
        });
        if (config.stagingEnvironment) {
            client.defaults.baseURL = `https://${config.stagingEnvironment}`;
        }
        else {
            client.defaults.baseURL = config.baseUrl || 'https://c1.adis.ws';
        }
        return client;
    }
    /**
     * Creates a parser which converts content JSON into classes and objects used by the SDK
     * @param config
     */
    createContentMapper(config) {
        return new ContentMapper(config);
    }
}
//# sourceMappingURL=data:application/json;base64,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